/**
 * useDescendantConversations — full descendant-tree discovery for the AI
 * Assistant anchor conversation (ENG-4404).
 *
 * HITL (credential) requests raised by subagents at depth 2..5 must surface in
 * the anchor's Marcus window exactly like direct-child requests do today. The
 * only depth-1 limit in the codebase is the id-set AIAssistant feeds
 * useInteractions; this hook generalizes it to the whole tree:
 *
 *   - LEVEL 1: the two existing useGetChildConversations primitives (user +
 *     tenant partition) — unchanged, they keep their own poll/WS plumbing.
 *   - CHAT DESCENDANTS (depths 2..5): dual-partition `parent_id` BFS via ONE
 *     useQueries fan-out. A user-partition node may have children in EITHER
 *     partition (partition placement is decided per spawn), so every frontier
 *     node gets BOTH a `user=true` and a tenant `parent_id:` probe.
 *   - HUNT FAMILIES: a tenant-partition node always carries `huntId` and its
 *     whole subtree shares it (spawn_agent_sync.go:117-124, :146-152 — Job
 *     inheritance), so ONE `hunt:<uuid>` query retrieves the entire tenant
 *     family; those nodes are never expanded per-node. Defensively, a tenant
 *     row WITHOUT huntId (anomalous) is expanded per-level like a user node.
 *
 * Assembly is a pure, cycle-guarded, depth-capped subtree walk
 * (buildDescendantTree) that admits ONLY anchor-reachable rows — the hunt
 * query returns sibling iteration transcripts from other runs of the same
 * hunt (workflow.go:90-94 mints a new parent_id:"self" root per run), and
 * surfacing those would widen the G2 interaction gate beyond the user's own
 * conversation tree. The subtree filter is a security requirement (P1-6a).
 *
 * Failure stance (security P1-7/P2-12 — fail-OPEN to depth-1): an errored or
 * 413'd fan-out query contributes no rows; the result degrades to whatever is
 * proven (always at least level-1 from the untouched primitives). retry:false
 * plus the 60s refetchInterval are the ONLY retry mechanism, and the error
 * path never issues a broader or unpartitioned query.
 */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import {
  FALLBACK_POLL_MS,
  INVALIDATE_DEBOUNCE_MS,
  INVALIDATE_MAX_WAIT_MS,
  useGetChildConversations,
} from '@/hooks/useChat';
import { getQueryKey, useUserKeySuffix } from '@/hooks/useQueryKeys';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { Conversation, UniqueQueryKeys } from '@/types';

/** UI mirror of backend MAX_SUBAGENT_DEPTH (spawn_agent.go:25; pinned by
 *  spawn_agent_test.go:244). Anchor = depth 0; deepest descendant = depth 5. */
export const UI_MAX_SUBAGENT_DEPTH = 5;

/** Total-id resource bound (security P0-5.3): bounds total request volume.
 *  Truncate BFS + console.warn once when hit — degrade, never hammer. */
export const UI_MAX_DESCENDANT_IDS = 200;

type Partition = 'user' | 'tenant';

/** staleTime for the fan-out probes: deliberately equal to the WS
 *  invalidation debounce so a burst of invalidations inside one debounce
 *  window coalesces into a single refetch per query, never several. */
const FANOUT_STALE_MS = INVALIDATE_DEBOUNCE_MS;

interface KnownRow {
  conv: Conversation;
  partitions: Set<Partition>;
}

type DiscoveryUnit =
  | { kind: 'parent'; nodeId: string; partition: Partition }
  | { kind: 'hunt'; huntId: string };

/** Child-edge predicate shared by BOTH walks (discovery + assembly) so the
 *  edge semantics cannot drift: parent_id === 'self' is the ROOT SENTINEL
 *  (conversation.go:25), never an edge, and a self-loop (id === parent) is
 *  never an edge. Parent matching itself is the byParent index lookup. */
function isChildEdge(id: string, parent: string | undefined): parent is string {
  return !!parent && parent !== 'self' && id !== parent;
}

/** Created-desc comparator shared by BOTH walks: the discovery walk must cap
 *  (P0-5.3) on the SAME per-parent child prefix buildDescendantTree
 *  assembles, so past the id cap the probed set and the surfaced set stay
 *  identical (cap-set equivalence). Equal `created` values (burst-spawned
 *  siblings at second resolution) tie-break on uuid/id ASCENDING: the two
 *  walks see siblings in DIFFERENT input orders (discovery: addRow insertion
 *  order; assembly: the id-sorted pool reconstructed from poolKey), so a
 *  stable sort alone would let a cap boundary inside a tie group select
 *  different sibling subsets — the tie-break makes cap-set equivalence exact
 *  regardless of input order. */
function compareCreatedDesc(a: KnownRow, b: KnownRow): number {
  const byCreated =
    (new Date(b.conv.created).getTime() || 0) -
    (new Date(a.conv.created).getTime() || 0);
  if (byCreated !== 0) return byCreated;
  const aId = a.conv.uuid || a.conv.id || '';
  const bId = b.conv.uuid || b.conv.id || '';
  return aId < bId ? -1 : aId > bId ? 1 : 0;
}

/** Fan-out/hunt query key: factory key + impersonation suffix appended LAST,
 *  mirroring useMy, so impersonation switches bucket the cache per principal
 *  (ENG-4400 precedent). The ONE composition point for both the render-time
 *  cache reads (keyFor) and the useQueries configs — the shapes cannot drift. */
function descendantQueryKey(
  anchorId: string,
  unit: DiscoveryUnit,
  userSuffix: string
): readonly unknown[] {
  return [
    ...getQueryKey.getDescendantConversations(anchorId, unit),
    ...(userSuffix ? [userSuffix] : []),
  ];
}

export interface DescendantConversations {
  /** All descendants of the anchor, depths 1..UI_MAX_SUBAGENT_DEPTH, deduped
   *  (first-occurrence-wins on `uuid || id`), BFS order (level, then created
   *  desc). */
  descendants: Conversation[];
  /** Ids observed in the user partition (loadable by the user-scoped chat
   *  loaders). */
  userDescendantIds: Set<string>;
  /** Ids observed ONLY in the tenant partition — generalizes AIAssistant's
   *  tenantOnlyChildIds: not navigable, surface in place. */
  tenantOnlyDescendantIds: Set<string>;
  isLoading: boolean;
}

/** Pure assembly: BFS from anchor over parent_id edges within `rows`.
 *  - parent_id === 'self' is the ROOT SENTINEL, never an edge (conversation.go:25)
 *  - visited set = cycle guard; maxDepth caps traversal
 *  - rows not reachable from anchor are EXCLUDED (G2 scope: hunt-family
 *    siblings from other iteration transcripts must not enter the id-set)
 *  - dedup on (uuid || id), first-occurrence-wins (AIAssistant union semantics) */
export function buildDescendantTree(
  anchorId: string,
  rows: Map<string, KnownRow>,
  maxDepth: number,
  maxIds: number = UI_MAX_DESCENDANT_IDS // P0-5.3: stop pushing once ordered.length hits maxIds; warn once
): {
  ordered: Conversation[];
  userIds: Set<string>;
  tenantOnlyIds: Set<string>;
  capped: boolean;
} {
  const byParent = new Map<string, string[]>();
  for (const [id, r] of rows) {
    const p = r.conv.parent_id;
    if (!isChildEdge(id, p)) continue;
    const siblings = byParent.get(p);
    if (siblings) siblings.push(id);
    else byParent.set(p, [id]);
  }
  const ordered: Conversation[] = [];
  const userIds = new Set<string>();
  const tenantOnlyIds = new Set<string>();
  const visited = new Set<string>([anchorId]);
  let capped = false;
  let frontier = [anchorId];
  for (
    let depth = 1;
    depth <= maxDepth && frontier.length > 0 && !capped;
    depth++
  ) {
    const next: string[] = [];
    for (const pid of frontier) {
      const kids = (byParent.get(pid) ?? [])
        .filter(id => !visited.has(id))
        .map(id => rows.get(id)!)
        .sort(compareCreatedDesc);
      for (const k of kids) {
        if (ordered.length >= maxIds) {
          capped = true; // P0-5.3
          break;
        }
        const id = k.conv.uuid || k.conv.id || '';
        if (!id || visited.has(id)) continue;
        visited.add(id);
        ordered.push(k.conv);
        if (k.partitions.has('user')) userIds.add(id);
        else tenantOnlyIds.add(id);
        next.push(id);
      }
      if (capped) break;
    }
    frontier = next;
  }
  return { ordered, userIds, tenantOnlyIds, capped };
}

export function useDescendantConversations(
  anchorId?: string
): DescendantConversations {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const userSuffix = useUserKeySuffix();

  // LEVEL 1 (reused primitives, unchanged): each already owns its 60s
  // fallback poll and partition-matched #conversation# WS invalidation.
  const level1User = useGetChildConversations(anchorId);
  const level1Tenant = useGetChildConversations(anchorId, { user: false });

  const keyFor = (unit: DiscoveryUnit): readonly unknown[] =>
    descendantQueryKey(anchorId ?? '', unit, userSuffix);

  // KNOWN-ROWS POOL + FRONTIER + HUNT SET — derived during render, no effects.
  // Discovery walks the anchor-reachable tree over rows already known (level-1
  // data plus each settled fan-out result read from the query cache; the
  // useQueries call below OBSERVES those same cache entries, so any data
  // change re-renders and this walk sees it). BFS converges one level per
  // query round trip; depth-5 nodes are never expanded (they cannot have
  // surfaced children), and beyond-cap nodes are never probed.
  const rows = new Map<string, KnownRow>();
  // Incrementally-maintained child index: kid lookup during the walk is O(1)
  // per parent instead of a per-frontier-node scan over the whole pool.
  const childrenByParent = new Map<string, string[]>();
  const addRow = (conv: Conversation, partition: Partition) => {
    const id = conv.uuid || conv.id || '';
    if (!id) return;
    const existing = rows.get(id);
    if (existing) {
      // Already-known id: only the partition set widens — never re-index,
      // or a dual-partition row would appear twice under its parent.
      existing.partitions.add(partition);
    } else {
      // First-occurrence-wins on the conv payload (dedup semantics), so the
      // indexed edge is the first occurrence's parent_id — exactly the edge
      // buildDescendantTree sees in the serialized pool.
      rows.set(id, { conv, partitions: new Set([partition]) });
      const p = conv.parent_id;
      if (isChildEdge(id, p)) {
        const siblings = childrenByParent.get(p);
        if (siblings) siblings.push(id);
        else childrenByParent.set(p, [id]);
      }
    }
  };
  for (const c of level1User.data ?? []) addRow(c, 'user');
  for (const c of level1Tenant.data ?? []) addRow(c, 'tenant');

  const frontierIds: string[] = [];
  const huntIdSet = new Set<string>();
  if (anchorId) {
    const registerHunt = (huntId: string) => {
      if (huntIdSet.has(huntId)) return;
      huntIdSet.add(huntId);
      const huntRows = queryClient.getQueryData<Conversation[]>(
        keyFor({ kind: 'hunt', huntId })
      );
      for (const r of huntRows ?? []) addRow(r, 'tenant');
    };

    const visited = new Set<string>([anchorId]);
    let discovered = 0;
    let frontier = [anchorId];
    let stop = false;
    for (
      let depth = 1;
      depth <= UI_MAX_SUBAGENT_DEPTH && frontier.length > 0 && !stop;
      depth++
    ) {
      const next: string[] = [];
      for (const pid of frontier) {
        // Candidate children of `pid`, snapshotted (`.filter` copies) before
        // merging cached probe data (which may add deeper rows mid-iteration)
        // and ordered by the SAME created-desc comparator assembly uses, so
        // the P0-5.3 cap truncates BOTH walks on the same prefix — the probed
        // set and the surfaced set never diverge (cap-set equivalence).
        const kidIds = (childrenByParent.get(pid) ?? [])
          .filter(id => !visited.has(id))
          .sort((a, b) => compareCreatedDesc(rows.get(a)!, rows.get(b)!));
        for (const id of kidIds) {
          if (discovered >= UI_MAX_DESCENDANT_IDS) {
            // P0-5.3: stop discovering — beyond-cap nodes are never probed.
            stop = true;
            break;
          }
          if (visited.has(id)) continue; // cycle guard
          visited.add(id);
          discovered++;
          const row = rows.get(id)!;
          if (row.partitions.has('tenant') && row.conv.huntId) {
            registerHunt(row.conv.huntId);
          }
          // Which partitions to fan out this node's parent_id over:
          //  - USER: always probed for an expandable node. A huntId-closed
          //    tenant node can still have a user-partition, huntId-less child —
          //    a non-Hunt-target subagent spawns in the initiating user's
          //    partition (conversation.go PartitionScope: no huntId + not public
          //    → {account}:{user}). Missing it drops that child's HITL row (the
          //    modelcost rollup probes both partitions for exactly this
          //    straddle — modelcost/service.go). This is FIX 1 (ENG-4404 F1).
          //  - TENANT: probed ONLY when the node is NOT huntId-closed. A tenant
          //    node WITH huntId has its whole tenant subtree delivered by the
          //    single hunt:<uuid> query, so a per-node tenant probe is
          //    redundant; an anomalous huntless tenant node (risk R6) still
          //    needs it.
          const huntClosed = row.partitions.has('tenant') && !!row.conv.huntId;
          // Expandable at all: any user-observed OR any tenant node
          // (huntId-closed nodes are still expanded — user-side only).
          const expandable =
            row.partitions.has('user') || row.partitions.has('tenant');
          if (depth < UI_MAX_SUBAGENT_DEPTH && expandable) {
            // Encode a huntId-closed node as "<id>|user" so the useQueries
            // builder emits its user-only probe set — the huntClosed decision
            // lives HERE only. The tag keeps frontierKey a stable primitive.
            frontierIds.push(huntClosed ? `${id}|user` : id);
            const probePartitions: Partition[] = huntClosed
              ? ['user']
              : ['user', 'tenant'];
            for (const partition of probePartitions) {
              const cached = queryClient.getQueryData<Conversation[]>(
                keyFor({ kind: 'parent', nodeId: id, partition })
              );
              for (const c of cached ?? []) addRow(c, partition);
            }
          }
          next.push(id);
        }
        if (stop) break;
      }
      frontier = next;
    }
  }

  // Serialized id-list keys (the useInteractions idsKey pattern): the
  // useQueries config list must only change when the DISCOVERED IDS change,
  // never on array identity churn (React 19 Compiler ON — constraint 6).
  const frontierKey = JSON.stringify([...frontierIds].sort());
  const huntsKey = JSON.stringify([...huntIdSet].sort());

  // Serialize the pool so the assembly memo has honest, stable deps.
  const poolKey = JSON.stringify(
    [...rows.entries()]
      .map(
        ([id, r]) =>
          [id, { conv: r.conv, partitions: [...r.partitions].sort() }] as const
      )
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
  );

  // useQueries FAN-OUT (dynamic list; rules-of-hooks-safe shape proven by
  // useCollaboratorQueries): per frontier node TWO partition probes, per
  // distinct huntId ONE family query. Per-unit stable keys give level
  // memoization for free — an unchanged node's result stays cached.
  const queryConfigs = useMemo(() => {
    if (!anchorId) return [];
    const frontier = JSON.parse(frontierKey) as string[];
    const hunts = JSON.parse(huntsKey) as string[];
    const units: DiscoveryUnit[] = [
      // Decode the walk's per-node probe decision: a "<id>|user" entry is a
      // huntId-closed node (user-only probe — FIX 1); a bare id probes both
      // partitions. The huntClosed decision is NOT recomputed here — it rides
      // the frontier tag so the walk stays the single source of truth.
      ...frontier.flatMap((entry): DiscoveryUnit[] => {
        const userOnly = entry.endsWith('|user');
        const nodeId = userOnly ? entry.slice(0, -'|user'.length) : entry;
        return userOnly
          ? [{ kind: 'parent', nodeId, partition: 'user' }]
          : [
              { kind: 'parent', nodeId, partition: 'user' },
              { kind: 'parent', nodeId, partition: 'tenant' },
            ];
      }),
      ...hunts.map((huntId): DiscoveryUnit => ({ kind: 'hunt', huntId })),
    ];
    return units.map(unit => ({
      queryKey: descendantQueryKey(anchorId, unit, userSuffix),
      queryFn: async () => {
        // Security P0-1/P1-8: params are ONLY label + key (+ user for the
        // user partition). Never allTenants/global/praetorian/red_team/osint
        // and never a username param — partition binding is the tenant
        // boundary here.
        const response = await axios.get('/my', {
          params: {
            label: 'conversation',
            key:
              unit.kind === 'parent'
                ? `parent_id:${unit.nodeId}`
                : `hunt:${unit.huntId}`,
            ...(unit.kind === 'parent' && unit.partition === 'user'
              ? { user: 'true' }
              : {}),
          },
        });
        // Single page only — HITL trees are small today (risk R1): a family
        // large enough to truncate the first /my page degrades to "deeper
        // nodes surface on a later poll / are absent", never to wrong data.
        // Option 3 (backend subtree endpoint) is the designated follow-up.
        return (response.data?.conversations ?? []) as Conversation[];
      },
      // Fail-OPEN to depth-1 (P1-7/P2-12): no tight retry; the 60s poll and
      // WS-driven invalidation are the only recovery paths.
      retry: false,
      refetchInterval: FALLBACK_POLL_MS,
      enabled: !!anchorId,
      staleTime: FANOUT_STALE_MS,
    }));
  }, [anchorId, frontierKey, huntsKey, userSuffix, axios]);

  const fanOut = useQueries({ queries: queryConfigs });

  // WS (fixed count, NOT per node — constraint 2): TWO broad #conversation#
  // prefix subscriptions (one per partition) invalidate the whole
  // [MY, 'conversation-descendants', anchorId] family, debounced 2s/5s.
  // Interaction rows themselves ride useInteractions' existing broad
  // #interaction# subscriptions — id-set discovery is the whole problem.
  const debouncedInvalidate = useDebouncedCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [UniqueQueryKeys.MY, 'conversation-descendants', anchorId],
      }),
    INVALIDATE_DEBOUNCE_MS,
    { maxWait: INVALIDATE_MAX_WAIT_MS }
  );
  // Subscribe rejected → the fan-out won't self-heal via WS. Log + invalidate
  // once so the 60s poll isn't the only recovery path (mirrors useChat).
  const onSubscribeError = (partition: Partition) => (reason: string) => {
    console.error('useDescendantConversations: subscribe rejected', {
      reason,
      partition,
      anchorId,
    });
    queryClient.invalidateQueries({
      queryKey: [UniqueQueryKeys.MY, 'conversation-descendants', anchorId],
    });
  };
  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    user: true,
    enabled: !!anchorId,
    onUpdate: debouncedInvalidate,
    onError: onSubscribeError('user'),
  });
  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    user: false,
    enabled: !!anchorId,
    onUpdate: debouncedInvalidate,
    onError: onSubscribeError('tenant'),
  });

  // ASSEMBLY: pure, cycle-guarded, depth-capped, anchor-reachable-only.
  // Reconstructing the pool from poolKey keeps the memo's deps honest and
  // string-stable (no raw array/object deps — constraint 6).
  const built = useMemo(() => {
    const entries = JSON.parse(poolKey) as Array<
      [string, { conv: Conversation; partitions: Partition[] }]
    >;
    const pool = new Map<string, KnownRow>(
      entries.map(([id, r]) => [
        id,
        { conv: r.conv, partitions: new Set(r.partitions) },
      ])
    );
    return buildDescendantTree(anchorId ?? '', pool, UI_MAX_SUBAGENT_DEPTH);
  }, [anchorId, poolKey]);

  // One console.warn per anchor PER MOUNTED INSTANCE when the total-id cap
  // trips (P0-5.3). Per-instance ref (not module state) so the bookkeeping
  // dies with the component — a module-level Set would grow unbounded across
  // logout/impersonation switches and never re-warn on a later session.
  const cappedWarnedAnchorsRef = useRef<Set<string>>(new Set());
  const capped = built.capped;
  useEffect(() => {
    if (capped && anchorId && !cappedWarnedAnchorsRef.current.has(anchorId)) {
      cappedWarnedAnchorsRef.current.add(anchorId);
      console.warn(
        `useDescendantConversations: descendant id cap (${UI_MAX_DESCENDANT_IDS}) reached for anchor ${anchorId}; truncating discovery (P0-5.3 — degrade, never hammer)`
      );
    }
  }, [capped, anchorId]);

  // TanStack Query v5 TRACKED PROPS (fix-loop N3): useQueries only notifies
  // this component when a property a render actually READ changes. Reading
  // only `isLoading` left settled queries silent — a background refetch (the
  // WS debouncedInvalidate, onSubscribeError's invalidate, or the 60s poll)
  // changes `data`/`dataUpdatedAt` but never `isLoading` (isPending stays
  // false), so fresh rows landed in the cache without a re-render and the
  // render-time getQueryData pool walk above never re-ran. Reading
  // `dataUpdatedAt` on EVERY result (reduce — deliberately no short-circuit,
  // unlike `.some`) subscribes the hook to data updates; the aggregate is a
  // stable primitive pair, so there is no array-identity churn and nothing
  // here feeds back into query configs or effects (no render loop).
  const fanOutState = fanOut.reduce(
    (acc, q) => ({
      isLoading: acc.isLoading || q.isLoading,
      // The READ of q.dataUpdatedAt is the subscription; timestamps are
      // non-decreasing, so the sum strictly increases on any refetch.
      dataStamp: acc.dataStamp + q.dataUpdatedAt,
    }),
    { isLoading: false, dataStamp: 0 }
  );

  const isLoading =
    Boolean(level1User.isLoading) ||
    Boolean(level1Tenant.isLoading) ||
    fanOutState.isLoading;

  return useMemo(
    () => ({
      descendants: built.ordered,
      userDescendantIds: built.userIds,
      tenantOnlyDescendantIds: built.tenantOnlyIds,
      isLoading,
    }),
    [built, isLoading]
  );
}
